import { useState } from 'react';
import { Search, TrendingDown } from 'lucide-react';
import { useHistoricoPrecos } from '@/features/compras/hooks/useHistoricoPrecos';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const HistoricoPrecos = () => {
  const [produtoId,    setProdutoId]    = useState('');
  const [fornecedorId, setFornecedorId] = useState('');
  const [buscar,       setBuscar]       = useState(false);

  const params = {
    produtoId:    produtoId    ? Number(produtoId)    : undefined,
    fornecedorId: fornecedorId ? Number(fornecedorId) : undefined,
    tamanhoPagina: 100,
  };

  const { data, isLoading } = useHistoricoPrecos(buscar ? params : {});
  const itens = data?.itens ?? [];

  const handleBuscar = () => setBuscar(true);

  const menor = itens.length > 0 ? Math.min(...itens.map(i => i.custoUnitario)) : null;
  const maior = itens.length > 0 ? Math.max(...itens.map(i => i.custoUnitario)) : null;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Histórico de Preços</h1>
        <p className="text-muted-foreground mt-1">Evolução do custo de compra por produto e fornecedor</p>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader><CardTitle className="text-base">Filtros</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="space-y-2">
              <Label>Produto ID</Label>
              <Input type="number" value={produtoId} onChange={e => setProdutoId(e.target.value)}
                placeholder="ID do produto" />
            </div>
            <div className="space-y-2">
              <Label>Fornecedor ID</Label>
              <Input type="number" value={fornecedorId} onChange={e => setFornecedorId(e.target.value)}
                placeholder="ID do fornecedor" />
            </div>
            <Button className="gap-2" onClick={handleBuscar}>
              <Search className="h-4 w-4" />Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      {itens.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Menor Custo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-green-600">
                {menor?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Maior Custo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-red-600">
                {maior?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <TrendingDown className="h-4 w-4" />Registros
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold">{itens.length}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Produto ID</TableHead>
                <TableHead>Fornecedor</TableHead>
                <TableHead className="text-right">Custo Unit.</TableHead>
                <TableHead className="text-right">Quantidade</TableHead>
                <TableHead>NF</TableHead>
                <TableHead>Recebimento</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!buscar ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Use os filtros acima para buscar o histórico de preços
                  </TableCell>
                </TableRow>
              ) : isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                </TableRow>
              ) : itens.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Nenhum registro encontrado
                  </TableCell>
                </TableRow>
              ) : itens.map(h => (
                <TableRow key={h.id}>
                  <TableCell>{new Date(h.dtReferencia).toLocaleDateString('pt-BR')}</TableCell>
                  <TableCell>{h.produtoId}</TableCell>
                  <TableCell>{h.nomeFornecedor ?? `ID ${h.fornecedorId}`}</TableCell>
                  <TableCell className="text-right font-mono">
                    {h.custoUnitario.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </TableCell>
                  <TableCell className="text-right font-mono">{h.quantidade}</TableCell>
                  <TableCell>{h.numeroNf ?? '—'}</TableCell>
                  <TableCell>{h.recebimentoId ? `#${h.recebimentoId}` : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default HistoricoPrecos;
